import React, { useCallback, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { GridColumn as Column, Grid } from '@progress/kendo-react-grid'
import { AffiliateDto } from 'api/Affiliate/AffiliateDto'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { useGridState } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'
import { fetchFilterAction } from 'store/identity/identity.actions'
import { AffiliateApiClient } from 'utils/api/affiliateApi'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { affiliateColumns, getAffiliateFilterOptions } from './affiliateColumn'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
  gridState: useAppSelector((state) => state.grid),
  hasFilters: useAppSelector(selectGridFilters),
})
const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const identityApiClient = useApiClient(IdentityApiClient)

  return {
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
  }
}
export const RecordsAffiliatesPage = (): JSX.Element => {
  const affiliateApiClient = useApiClient(AffiliateApiClient)

  const crumbs = [{ title: 'Affiliates', link: links.recordsAffiliates.url }]

  const { gridState, gridActions } = useGridState()
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [data, setData] = useState<AffiliateDto[]>()
  const { filters } = useMapStateToProps()
  const { fetchFilters, resetGridFilters } = useMapDispatchToProps()
  useEffect(() => {
    resetGridFilters()
    if (isEmpty(filters)) fetchFilters()
  }, [])

  useEffect(() => {
    fetchAffiliates()
  }, [gridState.pageSize, gridState.pageIndex, gridState.take])

  const fetchAffiliates = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const search = params?.query !== undefined ? params.query : getQuery()
      const pageIndex = gridState.pageIndex ?? 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = gridState.skip ?? 0
      const response = await affiliateApiClient.getAffiliates({
        search,
        pageIndex,
        pageSize,
      })

      if (response?.items) {
        setData(response.items)
      } else {
        setData([])
      }

      gridActions.setGridFilters({ take, skip, total: response.totalResults, pageIndex })
      setTableData({ ...tableData, meta: response })
    },
    [gridState, tableData]
  )

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      if (data === undefined) return null
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const handleSkipCountChange = async (skipCount: boolean) =>
    setTableData({ ...tableData, skipCount })

  const handlePageSizeChange = async (pageSize: number) =>
    gridActions.setGridFilters({ ...gridState, pageIndex: 1, pageSize, take: pageSize })

  const handleGridPageChange = async (pageIndex: number) =>
    gridActions.setGridFilters({ pageIndex })

  return (
    <React.Fragment>
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        setGridFilters={gridActions.setGridFilters}
        filterFetch={fetchAffiliates}
        filterType={FilterType.Affiliates}
        gridState={gridState}
        filterFields={getAffiliateFilterOptions()}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <Grid
          {...gridState}
          resizable
          data={data}
          total={gridState.total}
          take={gridState.take}
          skip={gridState.skip}
          pageable
          pager={() => (
            <Pager
              onSkipCountChange={handleSkipCountChange}
              onPageChange={handleGridPageChange}
              onPageSizeChange={handlePageSizeChange}
              page={gridState.pageIndex}
              pageSize={gridState.pageSize}
              take={gridState.take || 10}
              skip={gridState.skip || 0}
            />
          )}
        >
          {affiliateColumns
            .filter((c) => c.show !== false)
            .map((column, idx) => {
              return <Column key={idx} {...column} />
            })}
        </Grid>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}
